<template>
  <el-dialog
    title="账户充值"
    :visible="dialogVisible"
    width="520px"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="!submitting"
    @close="handleClose"
  >
    <el-alert
      v-if="requireReview"
      class="recharge-alert"
      title="当前家族充值规则要求审批，提交后将进入待审批流程。"
      type="warning"
      show-icon
      :closable="false"
    ></el-alert>
    <el-form
      :model="form"
      :rules="rules"
      label-position="top"
      class="recharge"
      ref="form"
    >
      <el-form-item label="广告账户 ID">
        <el-input
          :value="accountId"
          disabled
          placeholder="未选择广告账户"
        ></el-input>
      </el-form-item>
      <el-form-item
        label="账单归属"
        prop="billingOwnershipId"
      >
        <el-select
          v-model="form.billingOwnershipId"
          class="formItem"
          filterable
          placeholder="请选择账单归属"
          :loading="ownershipLoading"
          :disabled="!accountId || submitting"
        >
          <el-option
            v-for="item in ownerships"
            :key="item.id"
            :label="item.label"
            :value="item.id"
          ></el-option>
        </el-select>
        <p
          v-if="ownershipHint"
          class="recharge-hint"
        >
          {{ ownershipHint }}
        </p>
      </el-form-item>
      <el-form-item
        label="充值金额"
        prop="amount"
      >
        <el-input-number
          v-model="form.amount"
          class="formItem"
          :min="0.01"
          :max="1000000"
          :precision="2"
          :step="100"
          controls-position="right"
          placeholder="请输入充值金额"
          :disabled="submitting"
        ></el-input-number>
      </el-form-item>
      <el-form-item
        label="币种"
        prop="currency"
      >
        <el-select
          v-model="form.currency"
          class="formItem"
          :disabled="submitting"
        >
          <el-option
            label="USD（美元）"
            value="USD"
          ></el-option>
          <el-option
            label="CNY（人民币）"
            value="CNY"
          ></el-option>
          <el-option
            label="HKD（港币）"
            value="HKD"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="备注">
        <el-input
          v-model="form.remark"
          type="textarea"
          :rows="2"
          placeholder="请输入备注（可选）"
          :disabled="submitting"
        ></el-input>
      </el-form-item>
    </el-form>
    <div class="dp-flex justify-end align-item-c recharge-footer">
      <el-button
        type="primary"
        :loading="submitting"
        :disabled="ownershipLoading || !accountId"
        @click="submit"
      >
        提交申请
      </el-button>
      <el-button
        type="info"
        :disabled="submitting"
        @click="handleClose"
      >
        取消
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import {
  applyGoogleRecharge,
  getBillOwnershipsForRecharge,
  getGoogleRechargeAccountInfo,
  normalizeGoogleAccountId,
} from '@/api/adAsset/recharge';

const AMOUNT_MAX = 1000000;

export default {
  props: {
    dialogVisible: Boolean,
    account: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const validateAmount = (rule, value, callback) => {
      const n = Number(value);
      if (value === '' || value === null || value === undefined || Number.isNaN(n)) {
        callback(new Error('请输入充值金额'));
        return;
      }
      if (n < 0.01) {
        callback(new Error('充值金额不能小于 0.01'));
        return;
      }
      if (n > AMOUNT_MAX) {
        callback(new Error('单笔充值不能超过 1000000'));
        return;
      }
      callback();
    };
    return {
      submitting: false,
      ownershipLoading: false,
      requireReview: false,
      ownerships: [],
      ownershipHint: '',
      form: {
        billingOwnershipId: undefined,
        amount: undefined,
        currency: 'USD',
        remark: '',
      },
      rules: {
        billingOwnershipId: [{ required: true, message: '请选择账单归属', trigger: 'change' }],
        amount: [{ validator: validateAmount, trigger: 'blur' }],
        currency: [{ required: true, message: '请选择币种', trigger: 'change' }],
      },
    };
  },
  computed: {
    accountId() {
      return normalizeGoogleAccountId(this.account && (this.account.accountId || this.account.customerId));
    },
    preferredOwnershipId() {
      const raw = this.account && this.account.billAttributionId;
      if (raw == null || raw === '') {
        return undefined;
      }
      const id = Number(raw);
      return Number.isNaN(id) ? undefined : id;
    },
    agentId() {
      const raw = this.account && this.account.agencyId;
      if (raw == null || raw === '') {
        return undefined;
      }
      const id = Number(raw);
      return Number.isNaN(id) ? undefined : id;
    },
  },
  watch: {
    dialogVisible(visible) {
      if (visible) {
        this.resetForm();
        this.$nextTick(() => this.loadFormData());
      }
    },
  },
  methods: {
    unwrapList(res) {
      const data = res && res.data;
      if (Array.isArray(data)) {
        return data;
      }
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    },
    unwrapInfo(res) {
      const data = res && res.data;
      if (data && data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
        return data.data;
      }
      return data && typeof data === 'object' ? data : {};
    },
    ownershipLabel(item) {
      if (item.name) {
        return item.name;
      }
      return [item.familyName, item.agentName, item.settlementEntityName]
        .filter((part) => part != null && String(part).trim() !== '')
        .join('-');
    },
    toOwnershipId(value) {
      if (value == null || value === '') {
        return undefined;
      }
      const id = Number(value);
      return Number.isNaN(id) ? undefined : id;
    },
    mapOwnerships(list) {
      return list
        .map((item) => {
          const id = this.toOwnershipId(item.id);
          if (id == null) {
            return null;
          }
          return {
            id,
            label: this.ownershipLabel(item) || String(id),
          };
        })
        .filter(Boolean);
    },
    pickOwnershipId(options, list) {
      const preferred = this.preferredOwnershipId;
      if (preferred != null && options.some((item) => item.id === preferred)) {
        return preferred;
      }
      const apiDefault = list.length ? this.toOwnershipId(list[0]._defaultId || list[0].DefaultId) : undefined;
      if (apiDefault != null && options.some((item) => item.id === apiDefault)) {
        return apiDefault;
      }
      if (options.length === 1) {
        return options[0].id;
      }
      return undefined;
    },
    resetForm() {
      this.submitting = false;
      this.requireReview = false;
      this.ownerships = [];
      this.ownershipHint = '';
      const currency = (this.account && this.account.currency) || 'USD';
      this.form = {
        billingOwnershipId: undefined,
        amount: undefined,
        currency: currency || 'USD',
        remark: '',
      };
      if (this.$refs.form) {
        this.$refs.form.clearValidate();
      }
    },
    handleClose() {
      if (this.submitting) {
        return;
      }
      this.$emit('close');
      this.$emit('update:dialogVisible', false);
    },
    async fetchOwnerships(params) {
      const res = await getBillOwnershipsForRecharge(params);
      return this.unwrapList(res);
    },
    async loadFormData() {
      if (!this.accountId) {
        this.ownershipHint = '未选择广告账户';
        return;
      }
      this.ownershipLoading = true;
      this.ownershipHint = '';
      this.ownerships = [];
      this.form.billingOwnershipId = undefined;
      this.requireReview = false;
      const query = {
        actId: this.accountId,
        platform: 'GOOGLE',
      };
      if (this.agentId != null) {
        query.agentId = this.agentId;
      }
      const reviewPromise = getGoogleRechargeAccountInfo(this.accountId).catch(() => null);
      try {
        let list = await this.fetchOwnerships(query);
        const preferredMissing =
          this.preferredOwnershipId != null && !list.some((item) => this.toOwnershipId(item.id) === this.preferredOwnershipId);
        if ((list.length === 0 || preferredMissing) && this.agentId != null) {
          const fallback = await this.fetchOwnerships({
            actId: this.accountId,
            platform: 'GOOGLE',
          });
          if (fallback.length) {
            list = fallback;
          }
        }
        this.ownerships = this.mapOwnerships(list);
        this.form.billingOwnershipId = this.pickOwnershipId(this.ownerships, list);
        if (!this.ownerships.length) {
          this.ownershipHint = '当前家族没有可充值的账单归属（需已配置 Token）';
        }
        const reviewRes = await reviewPromise;
        if (reviewRes) {
          const info = this.unwrapInfo(reviewRes);
          this.requireReview = Boolean(info.requireReview);
          if (info.currency) {
            this.form.currency = info.currency;
          }
        }
      } catch (_error) {
        this.ownershipHint = '账单归属加载失败，请稍后重试';
      } finally {
        this.ownershipLoading = false;
      }
    },
    submit() {
      if (!this.accountId) {
        this.$message.warning('未选择广告账户');
        return;
      }
      this.$refs.form.validate((valid) => {
        if (!valid || this.submitting) {
          return;
        }
        this.submitting = true;
        applyGoogleRecharge({
          accountId: this.accountId,
          billingOwnershipId: this.form.billingOwnershipId,
          amount: this.form.amount,
          currency: this.form.currency,
          remark: this.form.remark || undefined,
        })
          .then(() => {
            this.$message.success('充值申请已提交，请到新资产管理查看结果');
            this.submitting = false;
            this.$emit('close');
            this.$emit('update:dialogVisible', false);
          })
          .catch(() => {
            this.submitting = false;
          });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.recharge {
  .formItem {
    width: 100%;
  }

  ::v-deep .el-input-number {
    width: 100%;

    .el-input__inner {
      text-align: left;
    }
  }
}

.recharge-alert {
  margin-bottom: 12px;
}

.recharge-hint {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #909399;
}

.recharge-footer {
  margin-top: 8px;

  .el-button + .el-button {
    margin-left: 8px;
  }
}
</style>
